// The TikTok accounts a canvas campaign's creators post on (docs/app-concept.md
// §4.8): each creator's fresh account of their own, where it is (being set up,
// warming up, posting), and what its videos brought. An account's handle shows
// once TikTok or our team has confirmed it; a click opens it on TikTok.

import {useEffect, useState} from 'react';
import type {ReactNode} from 'react';
import type {BrandCampaign, BrandCampaignAccount} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {describe, fetchCampaignAccounts} from './api';
import {InfoTip} from './info-tip';
import './agents.css';

const compact = new Intl.NumberFormat(undefined, {notation: 'compact', maximumFractionDigits: 1});
const WARMUP_DAYS = 3;

function stepLabel(account: BrandCampaignAccount): string {
  if (account.step === 'posting') return 'Posting';
  // Days done, so the count moves when the creator ticks a day off.
  if (account.step === 'warmup') return `Warm-up ${Math.min(account.warmup_days_done, WARMUP_DAYS)}/${WARMUP_DAYS}`;
  if (account.step === 'verify') return 'Being checked';
  return 'Setting up';
}

/** Under the handle: whose it is, its start and its last post. Without a handle the name is already the title. */
function accountLine(account: BrandCampaignAccount): string {
  return [
    account.handle ? account.creator_name : '',
    account.followers_at_start !== null ? `${compact.format(account.followers_at_start)} followers at start` : '',
    account.last_post_at ? `last post ${new Date(account.last_post_at).toLocaleDateString(undefined, {day: 'numeric', month: 'short'})}` : '',
  ].filter(Boolean).join(' · ');
}

/** TikTok's picture links expire after a few days: the TikTok mark stands in when one no longer loads. */
function AccountPicture({url}: {url: string}): ReactNode {
  const [broken, setBroken] = useState(false);
  return url && !broken ? <img className="account-avatar" src={url} alt="" onError={() => setBroken(true)} /> : <Icon name="tiktok" size={18} />;
}

function AccountRow({account}: {account: BrandCampaignAccount}) {
  const inner = <>
    <span className="key-icon"><AccountPicture url={account.avatar_url} /></span>
    <span className="key-who"><b>{account.handle ? `@${account.handle}` : account.creator_name}</b><small>{accountLine(account)}</small></span>
    <span className="post-views"><b>{account.posts}</b><small>{account.posts === 1 ? 'post' : 'posts'}</small></span>
    <span className="post-views"><b>{account.posts ? compact.format(account.views) : '–'}</b><small>views</small></span>
    <span className={`status-pill ${account.step === 'posting' ? 'active' : 'in_review'}`}>{stepLabel(account)}</span>
  </>;
  return account.handle
    ? <a className="post-row account-row" href={`https://www.tiktok.com/@${account.handle}`} target="_blank" rel="noopener">{inner}</a>
    : <div className="post-row account-row">{inner}</div>;
}

export function CampaignAccounts({campaign}: {campaign: BrandCampaign}) {
  const [accounts, setAccounts] = useState<BrandCampaignAccount[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    fetchCampaignAccounts(campaign.id).then((value) => { if (live) setAccounts(value.accounts); }).catch((caught) => { if (live) setError(describe(caught)); });
    return () => { live = false; };
  }, [campaign.id]);
  const posting = accounts?.filter((account) => account.step === 'posting').length ?? 0;
  return (
    <section className="card brand-section">
      <div className="section-head"><div><h2>Accounts<InfoTip text="Each creator posts on a fresh TikTok account of their own, made for this campaign. It warms up for three days before the first post. Its username shows once TikTok or our team has confirmed the account." /></h2></div></div>
      {error ? <p className="form-alert">{error}</p> : null}
      {accounts === null && !error ? <div className="empty-state"><span className="loader" /></div> : null}
      {accounts ? (
        accounts.length === 0 ? (
          <div className="empty-state"><Icon name="tiktok" size={28} /><b>No accounts yet</b><span>Each creator's account shows here once they join.</span></div>
        ) : (
          <>
            <div className="results-totals">
              <span><b>{accounts.length}</b>{accounts.length === 1 ? 'account' : 'accounts'}</span>
              <span><b>{posting}</b>posting</span>
              <span><b>{accounts.reduce((sum, account) => sum + account.posts, 0)}</b>posts</span>
              <span><b>{compact.format(accounts.reduce((sum, account) => sum + account.views, 0))}</b>views</span>
            </div>
            <div className="post-list">{accounts.map((account, index) => <AccountRow account={account} key={`${account.handle}-${index}`} />)}</div>
          </>
        )
      ) : null}
    </section>
  );
}
